import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';
// icons
import { Link } from 'lucide-react';
// components
import { PopoverButton } from '@newo/ui-kit';
import SelectPersonaForm from '@/modules/Sandbox/components/SelectPersonaForm';
// utils
import { useUpdateAgentMutation } from '@/services/designer/agents/agents.queries.ts';
import useSandboxStore from '@/modules/Sandbox/Sandbox.store.ts';
import { IChatPersona } from '@/modules/Chat/Chat.types.ts';
// assets
import styles from '@/modules/Sandbox/components/AgentControl/AgentControl.module.css';

interface AgentControlProps {
    agentId?: string;
    isFetching?: boolean;
}

const AgentControl = ({ agentId, isFetching }: AgentControlProps) => {
    const queryClient = useQueryClient();
    const { mutate: updateAgent } = useUpdateAgentMutation();
    const { agentPersona, setAgentPersona, setAgentActor } = useSandboxStore(
        useShallow((state) => ({
            agentPersona: state.agentPersona,
            setAgentPersona: state.setAgentPersona,
            setAgentActor: state.setAgentActor
        }))
    );

    let agentPersonaName = agentPersona ? agentPersona.name : 'Link persona';
    if (isFetching) {
        agentPersonaName = '';
    }

    const handlePersonaSelect = (persona?: IChatPersona) => {
        if (persona?.id !== agentPersona?.id) {
            setAgentActor(undefined);
        }
        setAgentPersona(persona);
        if (agentId) {
            updateAgent(
                {
                    agentId,
                    data: {
                        persona_id: persona?.id
                    }
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({
                            queryKey: ['agent', agentId]
                        });
                    }
                }
            );
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className="opacity-50">Agent:</div>
            <PopoverButton
                trigger={
                    <div className="flex">
                        <div className={styles.name}>{agentPersonaName}</div>
                        <Link className={styles.arrow} />
                    </div>
                }
            >
                {(close) => (
                    <SelectPersonaForm
                        isAgent
                        persona={agentPersona}
                        onClose={close}
                        onSubmit={handlePersonaSelect}
                    />
                )}
            </PopoverButton>
        </div>
    );
};

export default AgentControl;
